import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { ar } from 'date-fns/locale'

/**
 * Format date with timezone support
 */
export const formatDate = (date, formatStr = 'PPP', locale = 'en') => {
    if (!date) return ''

    const parsed = typeof date === 'string' ? parseISO(date) : date
    const options = locale === 'ar' ? { locale: ar } : {}

    return format(parsed, formatStr, options)
}

/**
 * Format relative time
 */
export const formatRelativeTime = (date, locale = 'en') => {
    if (!date) return ''

    const parsed = typeof date === 'string' ? parseISO(date) : date
    const options = locale === 'ar' ? { locale: ar, addSuffix: true } : { addSuffix: true }

    return formatDistanceToNow(parsed, options)
}

/**
 * Get status color for MUI Chip
 */
export const getStatusColor = (status) => {
    const colors = {
        new: 'info',
        read: 'error',
        replied: 'secondary',
        pending_department: 'warning',
        department_replied: 'primary',
        closed: 'default',
        archived: 'default',
    }
    return colors[status] || 'default'
}

/**
 * Get status background color
 */
export const getStatusBgColor = (status) => {
    const colors = {
        new: '#dbeafe',
        read: '#dcfce7',
        replied: '#f3e8ff',
        pending_department: '#fef3c7',
        department_replied: '#cffafe',
        closed: '#e2e8f0',
        archived: '#f1f5f9',
    }
    return colors[status] || '#f1f5f9'
}

/**
 * Get status text color
 */
export const getStatusTextColor = (status) => {
    const colors = {
        new: '#1d4ed8',
        read: '#15803d',
        replied: '#7c3aed',
        pending_department: '#b45309',
        department_replied: '#0891b2',
        closed: '#475569',
        archived: '#64748b',
    }
    return colors[status] || '#64748b'
}

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
}

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Generate a unique ID
 */
export const generateId = () => {
    return Math.random().toString(36).substring(2, 11)
}

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
    let timeout
    return (...args) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => func.apply(this, args), wait)
    }
}

/**
 * Deep clone object
 */
export const deepClone = (obj) => {
    return JSON.parse(JSON.stringify(obj))
}

/**
 * Check if object is empty
 */
export const isEmpty = (obj) => {
    if (obj === null || obj === undefined) return true
    if (typeof obj === 'string') return obj.trim() === ''
    if (Array.isArray(obj)) return obj.length === 0
    if (typeof obj === 'object') return Object.keys(obj).length === 0
    return false
}

/**
 * Get nested object value by path
 */
export const getNestedValue = (obj, path, defaultValue = null) => {
    const keys = path.split('.')
    let result = obj

    for (const key of keys) {
        if (result === null || result === undefined) return defaultValue
        result = result[key]
    }

    return result !== undefined ? result : defaultValue
}

/**
 * Parse JSON safely
 */
export const parseJSON = (str, defaultValue = null) => {
    try {
        return JSON.parse(str)
    } catch (e) {
        return defaultValue
    }
}

/**
 * Get bilingual value based on language
 */
export const getBilingualValue = (obj, field, language = 'en') => {
    if (!obj) return ''

    const arField = `${field}_ar`
    const enField = `${field}_en`

    if (language === 'ar') {
        return obj[arField] || obj[enField] || obj[field] || ''
    }

    return obj[enField] || obj[field] || obj[arField] || ''
}
